import axios from 'axios';
import config from '../config/environment';
import { recaptchaService } from '../utils/recaptchaService';

export interface FamilyMember {
  firstName: string;
  lastName: string;
  relationship: string;
  age: number;
  gotra?: string;
}

export interface PrimaryMember {
  firstName: string;
  lastName: string;
  gotra?: string;
}

export interface CalculatePricingRequest {
  global: boolean;
  primaryMember?: PrimaryMember;
  additionalFamilyMembers?: FamilyMember[];
}

export interface CalculatePricingResponse {
  calculatedMembersCount: number;
  basePrice: number;
  perMemberPrice: number;
  totalAmount: number;
  gstRate: number;
  gstAmount: number;
  finalAmount: number;
  pricingBreakdown: string;
}

export interface SeasonalPoojaBookingRequest {
  firstName: string;
  lastName: string;
  gotra?: string;
  place?: string;
  occasionName: string;
  preferredDate: string;
  specialRequirements?: string;
  global: boolean;
  additionalFamilyMembers: FamilyMember[];
}

export interface SeasonalPoojaBookingResponse {
  bookingUuid: string;
  firstName: string;
  lastName: string;
  gotra?: string;
  place?: string;
  occasionName: string;
  preferredDate: string;
  specialRequirements?: string;
  familyMembersCount: number;
  basePrice: number;
  perMemberPrice: number;
  totalAmount: number;
  gstAmount: number;
  finalAmount: number;
  paymentStatus: 'PENDING' | 'COMPLETED' | 'FAILED';
  bookingStatus: 'PAYMENT_PENDING' | 'CONFIRMED' | 'CANCELLED';
  razorpayKeyId: string;
  createdAt: string;
  familyMembers: FamilyMember[];
}

export interface PrasadamAddressRequest {
  bookingId: string;
  fullName: string;
  mobileNumber: string;
  addressLine1: string;
  city: string;
  state: string;
  pincode: string;
  landmark?: string;
}

export interface SeasonalPoojaPaymentRequest {
  bookingId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
}

export interface SeasonalPoojaPaymentResponse {
  orderId: string;
  razorpayOrderId: string;
  amount: number;
  currency: string;
  status: string;
  razorpayKeyId: string;
  seasonalBookingUuid: string;
  paymentId: string;
  customerEmail?: string;
  customerPhone?: string;
}

export interface ContactDetailsRequest {
  email: string;
  phone: number;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
}

export interface ContactDetailsResponse {
  bookingUuid: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: number;
  fullAddress: string;
  bookingStatus: string;
  paymentStatus: string;
  managementLink: string;
  finalAmount: number;
  occasionName: string;
  preferredDate: string;
  familyMembersCount: number;
  createdAt: string;
  updatedAt: string;
}

// Create axios instance for seasonal pooja API
const seasonalPoojaApi = axios.create({
  baseURL: config.authServerUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Debug logging for API configuration
console.log('🌐 Seasonal Pooja API Configuration:', {
  baseURL: config.authServerUrl,
  environment: process.env.REACT_APP_ENV,
  envAuthServerUrl: process.env.REACT_APP_AUTH_SERVER_URL,
  hostname: window.location.hostname,
  origin: window.location.origin,
  isAzureDeployment: window.location.hostname.includes('azurewebsites.net'),
  frontendDomain: window.location.origin,
  backendDomain: config.authServerUrl
});

// Azure CORS debugging
if (window.location.hostname.includes('azurewebsites.net')) {
  console.log('🔧 Azure Deployment Detected - CORS Configuration Required:', {
    frontendOrigin: window.location.origin,
    backendBaseURL: config.authServerUrl,
    requiredCorsHeader: `Access-Control-Allow-Origin: ${window.location.origin}`,
    requiredCorsHeaders: [
      'Access-Control-Allow-Origin',
      'Access-Control-Allow-Methods: GET, POST, PUT, DELETE, OPTIONS',
      'Access-Control-Allow-Headers: Content-Type, Authorization, x-requested-with',
      'Access-Control-Allow-Credentials: true'
    ]
  });
}

// Request interceptor for debugging
seasonalPoojaApi.interceptors.request.use(
  (config) => {
    console.log('📤 API Request:', {
      method: config.method?.toUpperCase(),
      url: `${config.baseURL}${config.url}`,
      params: config.params,
      data: config.data
    });
    return config;
  },
  (error) => {
    console.error('📤 Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for CORS and error handling
seasonalPoojaApi.interceptors.response.use(
  (response) => {
    console.log('📥 API Response Success:', {
      status: response.status,
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('📥 API Response Error:', {
      message: error.message,
      status: error.response?.status,
      url: error.config?.url,
      isCorsError: error.message.includes('CORS') || error.message.includes('preflight'),
      errorType: error.name
    });
    
    // Handle CORS errors with more informative messages
    if (error.message.includes('CORS') || error.message.includes('preflight')) {
      console.error('🚫 CORS Error Details:', {
        requestOrigin: window.location.origin,
        requestUrl: `${error.config?.baseURL}${error.config?.url}`,
        environment: process.env.REACT_APP_ENV,
        suggestion: 'This is a server-side CORS configuration issue. The backend needs to allow your origin.'
      });
    }
    
    return Promise.reject(error);
  }
);

// Seasonal Pooja Service
export const seasonalPoojaService = {


  /**
   * Calculate total price for seasonal pooja booking
   */
  calculatePricing: async (
    request: CalculatePricingRequest
  ): Promise<CalculatePricingResponse> => {
    try {
      console.log('💰 Calculating pricing for family members:', request);
      
      const recaptchaToken = await recaptchaService.getToken('calculate_seasonal_pooja_pricing');
      
      const response = await seasonalPoojaApi.post(
        '/public/seasonal-pooja/calculate-pricing-detailed',
        {
          ...request,
          recaptchaToken
        }
      );
      
      console.log('✅ Pricing calculated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error calculating pricing:', error);
      throw error;
    }
  },

  /**
   * Create seasonal pooja booking (guest user)
   */
  createBooking: async (
    bookingRequest: SeasonalPoojaBookingRequest
  ): Promise<SeasonalPoojaBookingResponse> => {
    try {
      console.log('📝 Creating seasonal pooja booking:', bookingRequest);
      
      const recaptchaToken = await recaptchaService.getToken('create_seasonal_pooja_booking');
      
      const response = await seasonalPoojaApi.post(
        '/public/seasonal-pooja/create-booking',
        {
          ...bookingRequest,
          recaptchaToken
        }
      );
      
      console.log('✅ Booking created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating booking:', error);
      throw error;
    }
  },

  /**
   * Create payment order for seasonal pooja
   */
  createPaymentOrder: async (
    bookingUuid: string,
    customerEmail?: string,
    customerPhone?: string
  ): Promise<SeasonalPoojaPaymentResponse> => {
    try {
      console.log('💳 Creating payment order for booking:', bookingUuid);
      
      const recaptchaToken = await recaptchaService.getToken('create_seasonal_pooja_payment');
      
      // Prepare request body - include customerEmail, customerPhone and recaptchaToken
      const requestBody: { customerEmail?: string; customerPhone?: string; recaptchaToken: string } = {
        recaptchaToken
      };
      if (customerEmail) {
        requestBody.customerEmail = customerEmail;
      }
      if (customerPhone) {
        requestBody.customerPhone = customerPhone;
      }
      
      const response = await seasonalPoojaApi.post(
        `/public/seasonal-pooja/${bookingUuid}/create-payment-order`,
        requestBody
      );
      
      console.log('✅ Payment order created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating payment order:', error);
      throw error;
    }
  },

  /**
   * Verify payment and update booking status
   */
  verifyPayment: async (
    bookingUuid: string,
    razorpayPaymentId: string,
    razorpayOrderId: string,
    razorpaySignature: string
  ): Promise<{ razorpay_payment_id: string; razorpay_order_id: string; razorpay_signature: string }> => {
    try {
      console.log('🔍 Verifying payment for booking:', bookingUuid);
      
      const recaptchaToken = await recaptchaService.getToken('verify_seasonal_pooja_payment');
      
      const response = await seasonalPoojaApi.post(
        `/public/seasonal-pooja/${bookingUuid}/verify-payment`,
        {
          razorpay_payment_id: razorpayPaymentId,
          razorpay_order_id: razorpayOrderId,
          razorpay_signature: razorpaySignature,
          recaptchaToken
        }
      );
      
      console.log('✅ Payment verified successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error verifying payment:', error);
      throw error;
    }
  },

  /**
   * Submit contact details and address information
   */
  submitContactDetails: async (
    bookingUuid: string,
    contactDetails: ContactDetailsRequest
  ): Promise<ContactDetailsResponse> => {
    try {
      console.log('📞 Submitting contact details for booking:', bookingUuid);
      
      const recaptchaToken = await recaptchaService.getToken('submit_contact_details');
      
      const response = await seasonalPoojaApi.post(
        `/public/seasonal-pooja/${bookingUuid}/add-contact-info`,
        {
          ...contactDetails,
          recaptchaToken
        }
      );
      
      console.log('✅ Contact details submitted successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error submitting contact details:', error);
      throw error;
    }
  },

  /**
   * Submit prasadam delivery address (legacy method - keeping for backward compatibility)
   */
  submitPrasadamAddress: async (
    addressRequest: PrasadamAddressRequest
  ): Promise<{ status: string; message: string }> => {
    try {
      console.log('📍 Submitting prasadam address for booking:', addressRequest.bookingId);
      
      const recaptchaToken = await recaptchaService.getToken('submit_prasadam_address');
      
      const response = await seasonalPoojaApi.post(
        '/public/seasonal-pooja/prasadam-address',
        addressRequest,
        {
          params: {
            recaptchaToken
          }
        }
      );
      
      console.log('✅ Prasadam address submitted successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error submitting prasadam address:', error);
      throw error;
    }
  },

  /**
   * Get booking details by ID
   */
  getBookingDetails: async (
    bookingUuid: string, 
    token?: string
  ): Promise<ContactDetailsResponse> => {
    try {
      console.log('📋 Fetching booking details:', bookingUuid);
      
      const recaptchaToken = await recaptchaService.getToken('get_seasonal_pooja_booking');
      
      const params: { recaptchaToken: string; token?: string } = {
        recaptchaToken
      };
      
      if (token) {
        params.token = token;
      }
      
      const response = await seasonalPoojaApi.get(
        `/seasonal-pooja/${bookingUuid}`,
        {
          params
        }
      );
      
      console.log('✅ Booking details fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching booking details:', error);
      throw error;
    }
  }
};
